<script lang="ts">
  import HoverButton from "./HoverButton.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import MenuTextInput from "./MenuTextInput.svelte";
  import Button from "$components/+blocks/Button/Button.svelte";
  import { createEventDispatcher } from "svelte";
  import { Alignment, Size } from "$components/editor/extensions/blocks/shared/attributes";
  import { AlignLeft, AlignCenter, AlignRight, A11y, TextEdit } from "$components/+blocks/OVIcon";
  import { fly } from "svelte/transition";
  import { alerts } from "app/stores/alert";

  export let width: Size;
  export let align: Alignment;
  export let alt = "";
  export let caption = "";
  export let parentType: "image" | "video" | undefined = undefined;

  let showAltTextInput = false;
  let existingAltText = alt;
  let altText = alt;

  let showCaptionInput = false;
  let existingCaption = caption;
  let captionText = caption;

  const dispatch = createEventDispatcher<{
    sizeChange: Size;
    alignChange: Alignment;
    altTextChange: string;
    captionChange: string;
  }>();
</script>

<div
  class="flex flex-col gap-y-1.5 rounded-lg border border-neutral-300 bg-white p-1 text-sm transition-[height] duration-200 ease-in-out dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-300"
>
  <!-- image size -->
  <div class="inline-flex gap-1">
    <HoverButton
      active={width === Size.ORIGINAL}
      on:click={() => dispatch("sizeChange", Size.ORIGINAL)}
    >
      Original
    </HoverButton>
    <HoverButton active={width === Size.SMALL} on:click={() => dispatch("sizeChange", Size.SMALL)}>
      Small
    </HoverButton>
    <HoverButton
      active={width === Size.MEDIUM}
      on:click={() => dispatch("sizeChange", Size.MEDIUM)}
    >
      Medium
    </HoverButton>
    <HoverButton active={width === Size.LARGE} on:click={() => dispatch("sizeChange", Size.LARGE)}>
      Large
    </HoverButton>
  </div>

  <Separator class="w-full bg-black/10 dark:bg-white/10" />

  <!-- alignment -->
  <div class="inline-flex gap-1">
    <HoverButton
      class="flex grow justify-start"
      active={align === Alignment.LEFT}
      disabled={width === Size.LARGE}
      a11yLabel="Align left"
      on:click={() => dispatch("alignChange", Alignment.LEFT)}
    >
      <OVIcon icon={AlignLeft} />
    </HoverButton>
    <HoverButton
      class="flex grow justify-center"
      active={align === Alignment.CENTER}
      disabled={width === Size.LARGE}
      a11yLabel="Align center"
      on:click={() => dispatch("alignChange", Alignment.CENTER)}
    >
      <OVIcon icon={AlignCenter} />
    </HoverButton>
    <HoverButton
      class="flex grow justify-end"
      active={align === Alignment.RIGHT}
      disabled={width === Size.LARGE}
      a11yLabel="Align right"
      on:click={() => dispatch("alignChange", Alignment.RIGHT)}
    >
      <OVIcon icon={AlignRight} />
    </HoverButton>
  </div>

  {#if parentType === "image" || parentType === "video"}
    <Separator class="w-full bg-black/10 dark:bg-white/10" />
  {/if}

  {#if parentType === "image"}
    <!-- alt text -->
    {#if showAltTextInput}
      <div class="flex w-full flex-col items-center gap-1" in:fly={{ y: -2 }}>
        <MenuTextInput
          placeholder="Add alt text"
          label="Alt text"
          icon={A11y}
          on:change={(e) => {
            altText = e.detail;
          }}
          value={altText}
        />

        <!-- only show action buttons when the text has changed from empty or from the existing text -->
        {#if altText !== existingAltText}
          <div class="flex w-full justify-between gap-1" in:fly={{ y: -4 }}>
            <Button
              isFullWidth
              label="Cancel"
              variant="tertiary"
              on:click={() => {
                showAltTextInput = false;
                altText = existingAltText;
              }}
            />
            <Button
              isFullWidth
              label="Update alt text"
              variant="system"
              on:click={() => {
                dispatch("altTextChange", altText);
                existingAltText = altText;
                showAltTextInput = false;
                alerts.success("Alt text updated");
              }}
            />
          </div>
        {/if}
      </div>
    {:else}
      <HoverButton
        class="flex w-full items-center justify-center gap-1"
        active={!!existingAltText}
        on:click={() => (showAltTextInput = true)}
      >
        <OVIcon icon={A11y} />
        <p class="text-sm">Alt text</p>
      </HoverButton>
    {/if}
    <Separator class="w-full bg-black/10 dark:bg-white/10" />
  {/if}

  <!-- caption (for both image and video) -->
  {#if parentType === "image" || parentType === "video"}
    {#if showCaptionInput}
      <div class="flex w-full flex-col items-center gap-1" in:fly={{ y: -2 }}>
        <MenuTextInput
          placeholder="Add caption"
          label="Caption"
          icon={TextEdit}
          on:change={(e) => {
            captionText = e.detail;
          }}
          value={captionText}
        />

        <!-- only show action buttons when the text has changed from empty or from the existing text -->
        {#if captionText !== existingCaption}
          <div class="flex w-full justify-between gap-1" in:fly={{ y: -4 }}>
            <Button
              isFullWidth
              label="Cancel"
              variant="tertiary"
              on:click={() => {
                showCaptionInput = false;
                captionText = existingCaption;
              }}
            />
            <Button
              isFullWidth
              label="Update caption"
              variant="system"
              on:click={() => {
                dispatch("captionChange", captionText);
                existingCaption = captionText;
                showCaptionInput = false;
                alerts.success("Caption updated");
              }}
            />
          </div>
        {/if}
      </div>
    {:else}
      <HoverButton
        class="flex w-full items-center justify-center gap-1"
        active={!!existingCaption}
        on:click={() => (showCaptionInput = true)}
      >
        <OVIcon icon={TextEdit} />
        <p class="text-sm">Caption</p>
      </HoverButton>
    {/if}
  {/if}
</div>
